import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { workSchema } from '../../utils/validation/schemas';
import { submitWorkApplication } from '../../lib/adminApi';
import type { WorkApplicationFormData } from '../../utils/validation/schemas';
import { Star, Mail, Phone, BookOpen, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../common/Button';

export const WorkApplicationForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState<File | null>(null);
  const [motivation, setMotivation] = useState('');
  const resumeInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  const { register, handleSubmit, formState: { errors }, reset } = useForm<WorkApplicationFormData>({
    resolver: zodResolver(workSchema),
    defaultValues: {
      specialty: '',
    }
  });

  const onSubmit = async (data: WorkApplicationFormData) => {
    if (loading) return;

    setLoading(true);
    try {
      await submitWorkApplication({
        name: data.name,
        email: data.email,
        phone: data.phone,
        experience: data.experience,
        motivation: motivation.trim() || undefined,
        specialty: data.specialty,
        resume,
      });
      toast.success('Candidatura enviada com sucesso! Entraremos em contato em breve.');
      reset();
      setResume(null);
      setMotivation('');
      if (resumeInputRef.current) resumeInputRef.current.value = '';
      navigate('/');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao enviar candidatura';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-lg p-6">
      <h2 className="text-xl font-bold text-purple-200 mb-6">Formulário de Candidatura</h2>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              Nome Completo
            </label>
            <input
              {...register('name')}
              className="input-primary"
              placeholder="Seu nome completo"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <Mail className="w-4 h-4 inline mr-2" />
              Email
            </label>
            <input
              type="email"
              {...register('email')}
              className="input-primary"
              placeholder="seu@email.com"
              disabled={loading}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <Phone className="w-4 h-4 inline mr-2" />
              Telefone
            </label>
            <input
              {...register('phone')}
              className="input-primary"
              placeholder="(00) 00000-0000"
              disabled={loading}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-400">{errors.phone.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-purple-200 mb-1">
              <BookOpen className="w-4 h-4 inline mr-2" />
              Especialidade
            </label>
            <select 
              {...register('specialty')} 
              className="input-primary"
              disabled={loading}
            >
              <option value="">Selecione sua especialidade</option>
              <option value="vedica">Astrologia Védica</option>
              <option value="karmica">Astrologia Kármica</option>
              <option value="taro">Tarô e Oráculos</option>
              <option value="numerologia">Numerologia</option>
            </select>
            {errors.specialty && (
              <p className="mt-1 text-sm text-red-400">{errors.specialty.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-1">
            Experiência Profissional
          </label>
          <textarea
            {...register('experience')}
            rows={4}
            className="input-primary"
            placeholder="Descreva sua experiência na área..."
            disabled={loading}
          />
          {errors.experience && (
            <p className="mt-1 text-sm text-red-400">{errors.experience.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-1">
            Motivação (opcional)
          </label>
          <textarea
            name="motivation"
            rows={3}
            className="input-primary"
            placeholder="Por que você quer ser consultor(a) aqui?"
            value={motivation}
            onChange={(e) => setMotivation(e.target.value)}
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-1">
            <Paperclip className="w-4 h-4 inline mr-2" />
            Currículo (opcional — PDF/DOC/DOCX até 5MB)
          </label>
          <input
            ref={resumeInputRef}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setResume(e.target.files?.[0] ?? null)}
            className="input-primary"
            disabled={loading}
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          loading={loading}
          className="w-full"
          icon={<Star className="w-5 h-5" />}
        >
          {loading ? 'Enviando...' : 'Enviar Candidatura'}
        </Button>
      </form>
    </div>
  );
};